// Fixed (non-scrollable) columns for the Direct Labor table, in display order.
// Widths (other than CLIN) are taken from detailedViewWidths.directLabor.firstTable in
// shared-resources/constants.js (same columns Detailed View uses: EMP ID/Sub ID=80, Employee
// Name=100, PO Line=65, PN=60, LCAT ID=65, LCAT Description=100), so both tables line up
// visually. CLIN has no Detailed View precedent here — its width (180) is chosen to keep this
// total in sync with ODC_FIXED_COLUMNS's own CLIN-prefixed total (weeklyEACODCColumns.js), so
// Direct Labor, Other Direct Costs and the Summary table (which pads itself out to match this
// total) all still line up.
export const DIRECT_LABOR_FIXED_COLUMNS = [
  { label: "CLIN", accessor: (employee) => employee.clin, width: 180 },
  {
    label: "EMP ID / Sub ID",
    accessor: (employee) => employee.employeeId,
    width: 80,
  },
  {
    label: "Employee Name",
    accessor: (employee) => employee.employeeName,
    width: 100,
  },
  { label: "NOTE", accessor: (employee) => employee.poLine, width: 65 },
  { label: "PN", accessor: (employee) => employee.pn, width: 60 },
  { label: "LCAT ID", accessor: (employee) => employee.lcatId, width: 65 },
  {
    label: "LCAT Description",
    accessor: (employee) => employee.lcatDescription,
    width: 100,
  },
];

// Total width of Direct Labor's fixed column table — the Weekly EAC Summary table's non-
// scrollable (label) column is padded out to this same total so both tables' scrollable regions
// start at the same X offset and their week columns line up.
export const DIRECT_LABOR_FIXED_TOTAL_WIDTH = DIRECT_LABOR_FIXED_COLUMNS.reduce(
  (total, column) => total + column.width,
  0,
);

export const SUMMARY_LABEL_COLUMN_WIDTH = 260;

export const SUMMARY_LABEL_FILLER_WIDTH = Math.max(
  DIRECT_LABOR_FIXED_TOTAL_WIDTH - SUMMARY_LABEL_COLUMN_WIDTH,
  0,
);

const billableUnbillableLabel = (employee) => {
  if (employee.billingStatus === true) return "Billable";
  if (employee.billingStatus === false) return "Unbillable";
  return undefined;
};

const yesNoLabel = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return undefined;
};

// Static scrollable columns — same set shown in Detailed View's Direct Labor table, minus
// Project Start Date, Project End Date and Revised Project End Date. Billable/Unbillable, Pay
// Type and Apply Burden are read-only values here instead of editable dropdowns. Widths are
// taken from detailedViewWidths.directLabor.secondTable in shared-resources/constants.js, which
// differs by contract type (Cost Plus vs FFP).
export const DIRECT_LABOR_STATIC_COLUMNS = [
  {
    label: "Account",
    accessor: (employee) => employee.acctName,
    width: { "Cost Plus": 120, FFP: 120 },
  },
  {
    label: "Company",
    accessor: (employee) => employee.company,
    width: { "Cost Plus": 120, FFP: 120 },
  },
  {
    label: "Base Cost Rate (Unloaded)",
    accessor: (employee) => employee.baseCostRateUnloaded,
    format: "currency",
    width: { "Cost Plus": 92, FFP: 100 },
  },
  {
    label: "Base Cost Rate (Loaded)",
    accessor: (employee) => employee.baseCostRateLoaded,
    format: "currency",
    width: { "Cost Plus": 92, FFP: 100 },
  },
  {
    label: "Labor Escalation %",
    accessor: (employee) => employee.laborEscalation,
    format: "percentRaw",
    width: { "Cost Plus": 92, FFP: 100 },
  },
  {
    label: "Labor Escalation Date",
    accessor: (employee) => employee.laborEscalationDate,
    format: "date",
    width: { "Cost Plus": 92, FFP: 100 },
  },
  {
    label: "Adjusted Bill Rate",
    accessor: (employee) => employee.adjustedBillRate,
    format: "currency",
    width: { "Cost Plus": 66, FFP: 100 },
  },
  {
    label: "Margin",
    accessor: (employee) => employee.margin,
    format: "percent",
    width: { "Cost Plus": 92, FFP: 100 },
  },
  {
    label: "Billable / Unbillable",
    accessor: billableUnbillableLabel,
    hideForType: "FFP",
    width: { "Cost Plus": 130, FFP: 0 },
  },
  {
    label: "Pay Type",
    accessor: (employee) => employee.payType,
    width: { "Cost Plus": 120, FFP: 120 },
  },
  {
    label: "Apply Burden",
    accessor: (employee) => yesNoLabel(employee.applyBurden),
    width: { "Cost Plus": 100, FFP: 100 },
  },
];

// Total width of Direct Labor's visible static scrollable columns (Account through Apply
// Burden) for a given contract type — used to pad the Weekly EAC Summary table with a matching
// leading empty column, so its week columns start at the same offset as Direct Labor's.
export const getStaticColumnsTotalWidth = (type) =>
  DIRECT_LABOR_STATIC_COLUMNS.filter(
    (column) => column.hideForType !== type,
  ).reduce((total, column) => total + column.width[type], 0);

// Weekly bucket sub-columns (Eff. Cost Rate/Hours/Burden Cost) — from
// detailedViewWidths.directLabor.thirdTable (first 3 of the monthly-recurring widths).
export const WEEKLY_BUCKET_COLUMN_WIDTHS = {
  costRate: 100,
  hours: 100,
  burdenCost: 100,
};

// ITD / Remaining Forecast / Total sub-columns — from
// detailedViewWidths.directLabor.fourtTable (Hours, Burden Cost).
export const DIRECT_LABOR_TOTALS_COLUMN_WIDTHS = {
  hours: 120,
  burdenCost: 100,
};

// Combined per-week / per-group widths so the Weekly EAC Summary table's single-value columns
// line up under the Direct Labor table's 3-subcolumn week groups and 2-subcolumn ITD/Remaining
// Forecast/Total groups (matches the borderless Margin table pattern in Detailed View, which has
// no header of its own and instead aligns to the table above it).
export const WEEKLY_COLUMN_WIDTH =
  WEEKLY_BUCKET_COLUMN_WIDTHS.costRate +
  WEEKLY_BUCKET_COLUMN_WIDTHS.hours +
  WEEKLY_BUCKET_COLUMN_WIDTHS.burdenCost;

export const TOTALS_GROUP_WIDTH =
  DIRECT_LABOR_TOTALS_COLUMN_WIDTHS.hours +
  DIRECT_LABOR_TOTALS_COLUMN_WIDTHS.burdenCost;
